/**
 * The sentence under the weight and reps boxes that says what number to type.
 *
 * Two questions the boxes cannot answer for themselves, and the app knew the
 * answer to both and never said it:
 *
 *   * **How many implements.** A dumbbell bench press uses two dumbbells. Type
 *     40 and you might mean one of them or both added up, and the volume
 *     differs by a factor of two. The library's own `increment_kg` already
 *     assumes one — 2 kg is a step on a rack, where a PAIR steps by 4 — and
 *     Farmer's Carry's description says "record the weight per hand" outright.
 *     Both facts were invisible at the moment of typing.
 *
 *   * **How many sides.** `is_unilateral` has been seeded correctly on 26 rows
 *     since the first migration and read by nothing but a text chip on the
 *     how-to card. Since 2026-09-15 a loaded one-sided exercise gets a row of
 *     boxes per side (lib/rounds), so the sentence no longer has to ask for one
 *     side's numbers — it says the two rows are separate, and still says how
 *     many implements.
 *
 * Real data on this instance says the ambiguity is not theoretical: read as
 * per-hand, two people's dumbbell presses come out at 63-70% of their barbell
 * equivalent, which is the normal ratio; a third logged an 80 lb shoulder press
 * beside a 25 lb hammer curl, which is not a pair of numbers one convention
 * produces.
 *
 * Stating it does not change any stored number, which is the point. Counting a
 * pair as double instead would silently double the history of everyone who had
 * already been logging the pair — and nothing in the data distinguishes them.
 */

import type { Exercise } from '../db/index.ts'

/** The sentence, or null when the boxes are unambiguous. */
export function entryHint(exercise: Exercise | null | undefined): string | null {
  if (!exercise) return null

  // "one dumbbell" rather than "per hand": a goblet squat and an overhead
  // triceps extension are one dumbbell held in two hands, and per-hand reads as
  // half of it. One implement's weight is right for every case.
  const implement =
    exercise.equipment === 'dumbbell' ? 'dumbbell'
    : exercise.equipment === 'kettlebell' ? 'kettlebell'
    : null

  // The odd bars come after the side question, not before: on a landmine press
  // "which side" is what you need at the moment of typing, and the plate
  // breakdown's absence explains itself by simply not being there.
  if (exercise.is_unilateral) {
    return implement
      ? `Left and right are logged separately — one ${implement}'s weight on each.`
      : 'Left and right are logged separately — what each side did, not both added up.'
  }

  if (implement) return `Log one ${implement}'s weight, not the pair added up.`

  // An EZ bar's own weight is anywhere from 15 to 30 lb, and a landmine loads
  // one end. Both made the plate breakdown a confident wrong answer, so it is
  // withheld — and this says why, rather than leaving a gap where a barbell
  // exercise usually has one.
  if (exercise.equipment === 'barbell' && exercise.bar_is_standard === false) {
    return "Log the total weight — this one isn't loaded like a standard bar,"
      + ' so there\'s no plate breakdown.'
  }

  return null
}
